import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import ThirdPartyServices from "../../services/ThirdParty";
import fire from "../../utils/Toastr";
import { HollowDotsSpinner } from "react-epic-spinners";

export default function CreateApplicationModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    applicationName: "",
    contactEmailAddress: "",
    callBackUrl: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.applicationName.trim()) {
      newErrors.applicationName = "Application name is required";
    } else if (formData.applicationName.length < 3) {
      newErrors.applicationName =
        "Application name must be at least 3 characters";
    }

    if (!formData.contactEmailAddress.trim()) {
      newErrors.contactEmailAddress = "Email address is required";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmailAddress)
    ) {
      newErrors.contactEmailAddress = "Please enter a valid email address";
    }

    if (!formData.callBackUrl.trim()) {
      newErrors.callBackUrl = "Callback URL is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await ThirdPartyServices.createApplication(formData);

      if (response.status) {
        fire(
          "success",
          response.message || "Application created successfully!",
        );

        // Call success callback with generated keys
        onSuccess(response.data);

        // Reset form
        setFormData({
          applicationName: "",
          contactEmailAddress: "",
          callBackUrl: "",
        });
        setErrors({});
      } else {
        fire("error", response.message || "Failed to create application");
      }
    } catch (error) {
      console.error("Error creating application:", error);
      fire("error", "An error occurred while creating the application");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      applicationName: "",
      contactEmailAddress: "",
      callBackUrl: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white p-6">
          <div className="flex justify-between items-center mb-6">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Create New Application
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="applicationName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Application Name *
              </label>
              <input
                type="text"
                id="applicationName"
                name="applicationName"
                value={formData.applicationName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.applicationName ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter application name"
                disabled={loading}
              />
              {errors.applicationName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.applicationName}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="contactEmailAddress"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Contact Email Address *
              </label>
              <input
                type="email"
                id="contactEmailAddress"
                name="contactEmailAddress"
                value={formData.contactEmailAddress}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.contactEmailAddress
                    ? "border-red-300"
                    : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter contact email"
                disabled={loading}
              />
              {errors.contactEmailAddress && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.contactEmailAddress}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                This email will be used for important notifications about this
                application.
              </p>
            </div>

            <div>
              <label
                htmlFor="callBackUrl"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Callback URL *
              </label>
              <input
                type="text"
                id="callBackUrl"
                name="callBackUrl"
                value={formData.callBackUrl}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.callBackUrl
                    ? "border-red-300"
                    : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter contact email"
                disabled={loading}
              />
              {errors.callBackUrl && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.callBackUrl}
                </p>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <h4 className="text-sm font-medium text-yellow-800 mb-1">
                Important Information
              </h4>
              <ul className="text-xs text-yellow-700 space-y-1">
                <li>
                  • API Key and Secret Key will be generated upon creation
                </li>
                <li>
                  • Store credentials securely - they cannot be retrieved later
                </li>
                <li>• You'll be shown the keys only once after creation</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <HollowDotsSpinner
                      color="#ffffff"
                      size={20}
                      className="mr-2"
                    />
                    Creating...
                  </>
                ) : (
                  "Create Application"
                )}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
